import {
  DiagnosticNote,
  DiagnosticStep,
  McpServerClientType,
  McpServerLookupFailure,
} from '@/api/user-portal-api-generated';

export const MCP_SPEC_AUTHORIZATION_URL =
  'https://modelcontextprotocol.io/specification/2026-07-28/basic/authorization';

const AUTH_SERVER_DISCOVERY_URL =
  'https://modelcontextprotocol.io/specification/2026-07-28/basic/authorization/authorization-server-discovery';

const CLIENT_REGISTRATION_URL =
  'https://modelcontextprotocol.io/specification/2026-07-28/basic/authorization/client-registration';

const RFC_9728_URL = 'https://datatracker.ietf.org/doc/html/rfc9728';
const RFC_8414_URL = 'https://datatracker.ietf.org/doc/html/rfc8414';
const RFC_7591_URL = 'https://datatracker.ietf.org/doc/html/rfc7591';

export interface DocLink {
  label: string;
  url: string;
}

export const STEP_LABELS: Record<DiagnosticStep, string> = {
  [DiagnosticStep.ADDRESS]: 'Address is a usable HTTPS URL',
  [DiagnosticStep.REACHABLE]: 'Server is reachable over TLS',
  [DiagnosticStep.CHALLENGE]: 'Server challenges unauthenticated requests',
  [DiagnosticStep.RESOURCE_METADATA]: 'Protected resource metadata (RFC 9728)',
  [DiagnosticStep.AUTH_SERVER_METADATA]:
    'Authorization server metadata (RFC 8414)',
  [DiagnosticStep.CLIENT_REGISTRATION]: 'A client can be obtained',
};

export const STEP_DESCRIPTIONS: Record<DiagnosticStep, string> = {
  [DiagnosticStep.ADDRESS]:
    'An absolute https:// address with no username or password in it.',
  [DiagnosticStep.REACHABLE]:
    'The address resolves, accepts a connection and presents a trusted certificate.',
  [DiagnosticStep.CHALLENGE]:
    'An MCP request without a token is refused with 401 and a WWW-Authenticate header.',
  [DiagnosticStep.RESOURCE_METADATA]:
    'The document naming which authorization servers issue tokens for this server.',
  [DiagnosticStep.AUTH_SERVER_METADATA]:
    'The authorization server publishes its authorize and token endpoints.',
  [DiagnosticStep.CLIENT_REGISTRATION]:
    'NewCore can get an OAuth client: a metadata document, dynamic registration, or one you register by hand.',
};

export interface Remediation {
  summary: string;
  fix: string;
  docs: DocLink[];
}

// coming_soon never reaches diagnose, so it is excluded; StepRow narrows it out.
export const FAILURE_REMEDIATIONS: Record<
  Exclude<McpServerLookupFailure, McpServerLookupFailure.COMING_SOON>,
  Remediation
> = {
  [McpServerLookupFailure.INVALID_URL]: {
    summary: 'This is not an address we can dial.',
    fix: 'Use an absolute https:// URL with no username or password in it. Plain http and credentials in the address are both refused.',
    docs: [{ label: 'MCP authorization', url: MCP_SPEC_AUTHORIZATION_URL }],
  },
  [McpServerLookupFailure.UNREACHABLE]: {
    summary: 'Nothing answered at this address.',
    fix: 'Check the host resolves publicly and the MCP endpoint path is right. A server reachable only inside a private network cannot be connected.',
    docs: [],
  },
  [McpServerLookupFailure.TLS_FAILED]: {
    summary: 'The TLS handshake failed.',
    fix: 'Serve a certificate from a publicly trusted CA that matches the hostname, and include the full intermediate chain. Self-signed certificates are refused.',
    docs: [],
  },
  [McpServerLookupFailure.TIMED_OUT]: {
    summary: 'The server took too long to answer.',
    fix: 'The handshake and each metadata fetch must complete inside ten seconds. A cold start that exceeds that will need to stay warm.',
    docs: [],
  },
  [McpServerLookupFailure.NOT_PROTECTED]: {
    summary: 'This address is not a protected MCP server.',
    fix: 'An MCP request without a token must be refused with 401 and a WWW-Authenticate header. A server that answers anonymously has no OAuth flow to connect to. The check stops at the challenge, so it confirms the address is OAuth-protected, not that it speaks MCP.',
    docs: [
      { label: 'MCP authorization', url: MCP_SPEC_AUTHORIZATION_URL },
      { label: 'RFC 9728', url: RFC_9728_URL },
    ],
  },
  [McpServerLookupFailure.NO_OAUTH_METADATA]: {
    summary: 'The server publishes no OAuth metadata we could read.',
    fix: 'Serve a protected resource metadata document at /.well-known/oauth-protected-resource (with the MCP path appended) and point at it from the 401 challenge. The authorization servers it names must each publish RFC 8414 or OpenID Connect discovery metadata.',
    docs: [
      {
        label: 'Authorization server discovery',
        url: AUTH_SERVER_DISCOVERY_URL,
      },
      { label: 'RFC 9728', url: RFC_9728_URL },
      { label: 'RFC 8414', url: RFC_8414_URL },
    ],
  },
  [McpServerLookupFailure.MALFORMED_METADATA]: {
    summary: 'The metadata was published but could not be used.',
    fix: 'Whichever document the failing step names: a resource document must parse as JSON and list at least one entry in authorization_servers, and an authorization server must publish both authorization_endpoint and token_endpoint as https URLs.',
    docs: [
      {
        label: 'Authorization server discovery',
        url: AUTH_SERVER_DISCOVERY_URL,
      },
      { label: 'RFC 8414', url: RFC_8414_URL },
    ],
  },
  [McpServerLookupFailure.RATE_LIMITED]: {
    summary: 'The metadata request was rate limited.',
    fix: 'Nothing is wrong with the server as such. Wait and run the test again.',
    docs: [],
  },
  [McpServerLookupFailure.SERVER_ERROR]: {
    summary: 'The metadata endpoint returned a server error.',
    fix: 'The well-known endpoint answered 5xx. Check the server logs for that route, then run the test again.',
    docs: [],
  },
};

export const NOTE_REMEDIATIONS: Record<DiagnosticNote, Remediation> = {
  [DiagnosticNote.NO_RESOURCE_METADATA_POINTER]: {
    summary:
      'The 401 did not say where your metadata lives, so we guessed from the origin.',
    fix: 'Add a resource_metadata parameter to the WWW-Authenticate header naming the full URL of your protected resource metadata document. Guessing works here, but a client that does not guess will fail to connect.',
    docs: [
      {
        label: 'Authorization server discovery',
        url: AUTH_SERVER_DISCOVERY_URL,
      },
      { label: 'RFC 9728', url: RFC_9728_URL },
    ],
  },
  [DiagnosticNote.NO_AUTHORIZATION_SERVERS]: {
    summary: 'The resource metadata listed no authorization servers.',
    fix: 'Populate authorization_servers with at least one issuer URL. An empty list tells clients there is nowhere to get a token.',
    docs: [{ label: 'RFC 9728', url: RFC_9728_URL }],
  },
  [DiagnosticNote.NO_HTTPS_AUTHORIZATION_SERVERS]: {
    summary:
      'Your authorization servers are listed, but none is an https address, so none was fetched.',
    fix: 'List each issuer in authorization_servers as an https URL. NewCore only talks to authorization servers over https, so http, credential-bearing or unparseable entries are skipped.',
    docs: [{ label: 'RFC 9728', url: RFC_9728_URL }],
  },
  [DiagnosticNote.REGISTRATION_ENDPOINT_NOT_HTTPS]: {
    summary: 'Your registration endpoint is not https, so it was ignored.',
    fix: 'Publish registration_endpoint as an https URL. Until then an admin has to register a client by hand instead of NewCore registering one on demand.',
    docs: [
      { label: 'Client registration', url: CLIENT_REGISTRATION_URL },
      { label: 'RFC 7591', url: RFC_7591_URL },
    ],
  },
  [DiagnosticNote.DOCUMENTATION_NOT_HTTPS]: {
    summary: 'Your documentation link is not https, so it was ignored.',
    fix: 'Publish service_documentation as an https URL and it will be shown to admins setting this server up.',
    docs: [{ label: 'RFC 8414', url: RFC_8414_URL }],
  },
  [DiagnosticNote.NO_TOKEN_AUTH_METHODS]: {
    summary: 'Your authorization server advertises no token auth methods.',
    fix: 'Publish token_endpoint_auth_methods_supported. Without it a client carrying a secret falls back to client_secret_basic, which fails if your server expects something else.',
    docs: [{ label: 'RFC 8414', url: RFC_8414_URL }],
  },
  [DiagnosticNote.UNUSABLE_RESOURCE_METADATA_POINTER]: {
    summary:
      'Your 401 named a metadata address we could not use, so we guessed from the origin.',
    fix: 'The resource_metadata parameter must be an absolute https URL. A relative path, a plain http address or an unparseable value is ignored, and a client that does not fall back to guessing will fail to connect.',
    docs: [
      {
        label: 'Authorization server discovery',
        url: AUTH_SERVER_DISCOVERY_URL,
      },
      { label: 'RFC 9728', url: RFC_9728_URL },
    ],
  },
  [DiagnosticNote.CIMD_WITHOUT_S256]: {
    summary:
      'Client ID metadata documents are advertised, but the S256 PKCE challenge is not.',
    fix: 'Add S256 to code_challenge_methods_supported. Without it the metadata-document path cannot be used and we fall back to a client carrying a secret.',
    docs: [{ label: 'Client registration', url: CLIENT_REGISTRATION_URL }],
  },
};

export const CLIENT_TYPE_LABELS: Record<McpServerClientType, string> = {
  [McpServerClientType.METADATA_DOCUMENT]: 'Client ID metadata document',
  [McpServerClientType.DCR]: 'Dynamic client registration',
  [McpServerClientType.PRE_REGISTERED]: 'Pre-registered client',
};

export const CLIENT_TYPE_DESCRIPTIONS: Record<McpServerClientType, string> = {
  [McpServerClientType.METADATA_DOCUMENT]:
    'NewCore identifies itself with a hosted document. Nothing to type in.',
  [McpServerClientType.DCR]:
    'Your server offers registration on demand. Nothing to type in, unless it refuses our redirect URI — some providers accept only localhost — and then set-up asks for a client registered by hand.',
  [McpServerClientType.PRE_REGISTERED]:
    'An admin registers a client at your server and enters it: an ID and secret, or an ID alone for a public client.',
};

export const IMPLEMENTATION_GUIDES: DocLink[] = [
  {
    label: 'MCP specification — Authorization',
    url: MCP_SPEC_AUTHORIZATION_URL,
  },
  {
    label: 'Python (FastMCP) — Authorization',
    url: 'https://py.sdk.modelcontextprotocol.io/v1/authorization/',
  },
  {
    label: 'TypeScript SDK — Serving authorization',
    url: 'https://github.com/modelcontextprotocol/typescript-sdk/blob/main/docs/serving/authorization.md',
  },
  {
    label: 'Cloudflare Workers — MCP authorization',
    url: 'https://developers.cloudflare.com/agents/model-context-protocol/authorization/',
  },
];
